import React from 'react';
import styled from 'styled-components';

import { createMarkup, useFormikHandles } from 'app/utils';
import { Box, Checkbox, Flex, HelperText, Input, Label, Radio, Select, Typography, Switch, Icons } from 'components';

import { FormGeneratorState } from './types';

interface FormGeneratorProps {
  formItens: FormGeneratorState[][];
  formik: any;
  upperLabel?: boolean;
}

const InfoIcon = styled((props) => <Icons {...props} />)`
  width: 20px;
  color: ${({ theme }) => theme.colors.primary_400};
  cursor: pointer;
`;

const Tooltip = styled.div`
  background: ${({ theme }) => theme.colors.dark_350};
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  border-radius: 4px;
  display: none;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${({ theme }) => theme.colors.dark_350};
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const InfoWrapper = styled((props) => <Box {...props} />)`
  &:hover {
    ${Tooltip} {
      display: block;
    }
  }
`;

const FormGenerator = ({ formItens, formik, upperLabel = true }: FormGeneratorProps) => {
  const { formikHandleChange, formikHandleFocus, formikHandleBlur } = useFormikHandles(formik);

  return (
    <>
      {formItens.map((rowItem, rowIndex) => {
        return (
          <Flex
            key={`form-first-${rowIndex.toString()}`}
            data-wrapper={`form-first-${rowIndex.toString()}`}
            flexGrow={1}
            flexDirection={{ m: 'column', d: 'row' }}
            flexWrap={{ m: 'wrap', d: 'nowrap' }}
            alignItems="flex-start"
            width="100%"
          >
            {rowItem.map((item, index) => {
              return item.hide ? (
                <React.Fragment key={`form-first-item-${item.name}-${index.toString()}`} />
              ) : (
                <Flex
                  key={`form-first-item-${item.name}-${index.toString()}`}
                  flexGrow={item.flexGrow || 1}
                  // bg="red"
                  // flexDirection={{ m: 'column', d: 'row' }}
                  // flexWrap={{ m: 'wrap', d: 'nowrap' }}
                  width={{ m: '100%', d: 'inherit' }}
                >
                  <Flex
                    flexDirection="column"
                    mt={item.marginTop === 0 ? 0 : item.marginTop ? item.marginTop : { m: 3, d: 4 }}
                    // // mb={item.formType === 'switch' ? 0 : 4}
                    // mb={rowIndex === formItens.length - 1 ? 0 : 4}
                    ml={{ d: index ? 3 : 0 }}
                    width={item && item.width ? item.width : 'inherit'}
                    maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                    flexShrink={0}
                    flexGrow={1}
                  >
                    {item.formType !== 'switch' && (
                      <Flex justifyContent="space-between">
                        <Flex flexGrow={0}>
                          <Typography type="type1" fontWeight="bold" upper={upperLabel}>
                            <Label htmlFor={`item-${item.name}`}>{item.label}</Label>
                          </Typography>
                        </Flex>

                        {!!item.rightLabel && (
                          <Flex display={{ m: 'none', d: 'flex' }} flexGrow={0}>
                            <Typography type="type0" color="gray_500">
                              {item.rightLabel}
                            </Typography>
                          </Flex>
                        )}
                      </Flex>
                    )}

                    <Flex flexDirection="column">
                      <Flex mt={1} flexWrap="nowrap" alignItems="center">
                        <Flex flexGrow={1} flexWrap="nowrap" alignItems="center">
                          {item.before && item.before.length ? (
                            item.before.map((itemBefore, indexBefore) => {
                              return (
                                <Box
                                  flexGrow={0}
                                  key={`form-itemBefore-${itemBefore.name}-${indexBefore.toString()}`}
                                  width={itemBefore.width ? itemBefore.width : 'inherit'}
                                  maxWidth={itemBefore.maxWidth ? itemBefore.maxWidth : 'inherit'}
                                  mr={2}
                                >
                                  {itemBefore.formType === 'input' && (
                                    <>
                                      <Input
                                        id={`form-itemBefore-input-${itemBefore.name}`}
                                        name={itemBefore.name}
                                        type={itemBefore.inputType}
                                        onChange={itemBefore.inputOnChange || formikHandleChange}
                                        onFocus={itemBefore.inputOnFocus || formikHandleFocus}
                                        onBlur={itemBefore.inputOnBlur || formikHandleBlur}
                                        onKeyDown={itemBefore.inputOnKeyDown || undefined}
                                        value={
                                          (itemBefore.value
                                            ? itemBefore.value
                                            : itemBefore.mask
                                            ? itemBefore.mask(formik.values[itemBefore.name])
                                            : formik.values[itemBefore.name]) || ''
                                        }
                                        maxLength={itemBefore.maxLength}
                                        data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                        placeholder={itemBefore.placeholder || ''}
                                        data-removearrows={!!itemBefore.inputNumberRemoveArrows}
                                        disabled={!!itemBefore.disabled}
                                        data-preventautocomplete={itemBefore.preventAutoComplete || false}
                                        readOnly={itemBefore.readOnly || itemBefore.preventAutoComplete || false}
                                      />
                                    </>
                                  )}

                                  {itemBefore.formType === 'select' && (
                                    <Select
                                      id={`form-itemBefore-select-${itemBefore.name}`}
                                      name={itemBefore.name}
                                      data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                      onChange={itemBefore.selectOnChange || formikHandleChange}
                                      disabled={!!itemBefore.disabled}
                                      defaultValue={itemBefore.defaultValue || formik.values[itemBefore.name]}
                                    >
                                      {!itemBefore.selectRemoveFirstOption && (
                                        <option value={itemBefore.selectFirstValue} hidden>
                                          {itemBefore.selectFirstLabel || ''}
                                        </option>
                                      )}
                                      {itemBefore.options &&
                                        itemBefore.options.map((optionItem, indexSelectIndex) => (
                                          <option
                                            key={`form-itemBefore-option-item-${itemBefore.name}-${indexSelectIndex.toString()}`}
                                            // selected={formik.values[itemBefore.name] === optionItem.value}
                                            value={optionItem.value}
                                          >
                                            {optionItem.label}
                                          </option>
                                        ))}
                                    </Select>
                                  )}
                                </Box>
                              );
                            })
                          ) : (
                            <></>
                          )}

                          <Box
                            flexGrow={0}
                            key={`form-item-box-${index.toString()}-${item.name}`}
                            width={item.width ? item.width : 'inherit'}
                            maxWidth={item.maxWidth ? item.maxWidth : 'inherit'}
                            position="relative"
                          >
                            {item.formType === 'input' && (
                              <>
                                {!!item.inputSufixIcon && <>{item.inputSufixIcon}</>}

                                <Input
                                  id={`form-item-input-${item.name}`}
                                  name={item.name}
                                  type={item.inputType}
                                  onChange={item.inputOnChange || formikHandleChange}
                                  onFocus={item.inputOnFocus || formikHandleFocus}
                                  onBlur={item.inputOnBlur || formikHandleBlur}
                                  onKeyDown={item.inputOnKeyDown || undefined}
                                  value={(item.value ? item.value : item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]) || ''}
                                  maxLength={item.maxLength}
                                  data-error={formik.touched[item.name] && formik.errors[item.name]}
                                  placeholder={item.placeholder || ''}
                                  data-removearrows={!!item.inputNumberRemoveArrows}
                                  disabled={!!item.disabled}
                                  data-preventautocomplete={item.preventAutoComplete || false}
                                  readOnly={item.readOnly || item.preventAutoComplete || false}
                                />
                              </>
                            )}

                            {item.formType === 'select' && (
                              <Select
                                id={`form-item-select-${item.name}`}
                                name={item.name}
                                data-error={formik.touched[item.name] && formik.errors[item.name]}
                                onChange={item.selectOnChange || formikHandleChange}
                                disabled={!!item.disabled}
                                defaultValue={item.defaultValue || formik.values[item.name]}
                              >
                                {!item.selectRemoveFirstOption && (
                                  <option value={item.selectFirstValue} hidden>
                                    {item.selectFirstLabel}
                                  </option>
                                )}
                                {item.options &&
                                  item.options.map((optionItem, selectIndex) => (
                                    <option
                                      key={`option-item-${item.name}-${selectIndex.toString()}`}
                                      // selected={formik.values[item.name] === optionItem.value}
                                      value={optionItem.value}
                                    >
                                      {optionItem.label}
                                    </option>
                                  ))}
                              </Select>
                            )}

                            {item.formType === 'radio' && (
                              <Flex flexDirection={item.radioDirection || 'row'}>
                                {item.options?.map((radioOptionItem, radioOptionItemIndex) => {
                                  return (
                                    <Flex
                                      key={`form-item-input-radio-${item.name}-${radioOptionItemIndex.toString()}`}
                                      ml={radioOptionItemIndex && (item.radioDirection || 'row') === 'row' ? 12 : 0}
                                    >
                                      <Radio
                                        id={`radioItem-${item.name}-${radioOptionItem.value}`}
                                        name={item.name}
                                        type="radio"
                                        disabled={!!item.disabled}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          formik.setFieldValue(item.name, Number(e.target.value));
                                        }}
                                        value={radioOptionItem.value}
                                        checked={formik.values[item.name] === radioOptionItem.value}
                                      />

                                      <label htmlFor={`radioItem-${item.name}-${radioOptionItem.value}`} style={{ cursor: 'pointer' }}>
                                        <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                          {radioOptionItem.label}
                                        </Typography>
                                      </label>
                                    </Flex>
                                  );
                                })}
                              </Flex>
                            )}

                            {item.formType === 'checkbox' && (
                              <Flex flexDirection={item.checkboxDirection || 'row'}>
                                {item.options && item.options.length ? (
                                  item.options?.map((checkboxOptionItem, checkboxOptionItemIndex) => {
                                    return (
                                      <Flex
                                        key={`form-item-input-checkbox-${item.name}-${checkboxOptionItemIndex.toString()}`}
                                        ml={checkboxOptionItemIndex && (item.checkboxDirection || 'row') === 'row' ? 12 : 0}
                                        alignItems="center"
                                      >
                                        <Checkbox
                                          id={`checkboxItem-${item.name}-${checkboxOptionItem.value}`}
                                          name={item.name}
                                          type="checkbox"
                                          disabled={!!item.disabled}
                                          onChange={item.checkboxOnChange || formikHandleChange}
                                          // onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                          //   formik.setFieldValue(item.name, Number(e.target.value));
                                          // }}
                                          value={checkboxOptionItem.value}
                                          checked={checkboxOptionItem.active}
                                        />

                                        <label htmlFor={`checkboxItem-${item.name}-${checkboxOptionItem.value}`} style={{ cursor: 'pointer' }}>
                                          <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                            {checkboxOptionItem.label}
                                          </Typography>
                                        </label>
                                      </Flex>
                                    );
                                  })
                                ) : (
                                  <Typography type="type1" color="alert_400" fontWeight="bold">
                                    {item.checkboxOptionsEmptyMessage || ''}
                                  </Typography>
                                )}
                              </Flex>
                            )}

                            {item.formType === 'switch' && (
                              <>
                                <Flex mt={4}>
                                  <Switch
                                    checked={item.checked || false}
                                    color={item.checked ? 'green' : 'gray'}
                                    onClick={() => formik.setFieldValue(item.name, !item.checked)}
                                  />

                                  <Typography
                                    type="type2"
                                    ml={2}
                                    color="black50"
                                    onClick={() => formik.setFieldValue(item.name, !item.checked)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {item.label}
                                  </Typography>
                                </Flex>

                                {/* <Input
                                id={`item-${item.name}`}
                                name={item.name}
                                type={item.inputType}
                                onChange={item.inputOnChange || formikHandleChange}
                                onFocus={formikHandleFocus}
                                onBlur={formikHandleBlur}
                                value={
                                  item.value
                                    ? item.value
                                    : item.mask
                                    ? item.mask(formik.values[item.name])
                                    : formik.values[item.name]
                                }
                                maxLength={item.maxLength}
                                data-error={formik.touched[item.name] && formik.errors[item.name]}
                                placeholder={item.placeholder || ''}
                                data-removearrows={!!item.inputNumberRemoveArrows}
                                disabled={!!item.disabled}
                              /> */}
                              </>
                            )}
                          </Box>

                          {!!item.info && (
                            <Box ml={1}>
                              <InfoWrapper width={20} height={20} flexShrink={0} position="relative">
                                <Tooltip>{item.info}</Tooltip>
                                <InfoIcon icon="info" />
                              </InfoWrapper>
                            </Box>
                          )}

                          {item.after && item.after.length ? (
                            item.after.map((itemAfter, indexBefore) => {
                              return (
                                <Box
                                  flexGrow={0}
                                  key={`form-itemAfter-${itemAfter.name}-${indexBefore.toString()}`}
                                  width={itemAfter.width ? itemAfter.width : 'inherit'}
                                  maxWidth={itemAfter.maxWidth ? itemAfter.maxWidth : 'inherit'}
                                  ml={2}
                                >
                                  {itemAfter.formType === 'input' && (
                                    <Input
                                      id={`item-${itemAfter.name}`}
                                      name={itemAfter.name}
                                      type={itemAfter.inputType}
                                      onChange={itemAfter.inputOnChange || formikHandleChange}
                                      onFocus={itemAfter.inputOnFocus || formikHandleFocus}
                                      onBlur={itemAfter.inputOnBlur || formikHandleBlur}
                                      onKeyDown={itemAfter.inputOnKeyDown || undefined}
                                      value={
                                        (itemAfter.value
                                          ? itemAfter.value
                                          : itemAfter.mask
                                          ? itemAfter.mask(formik.values[itemAfter.name])
                                          : formik.values[itemAfter.name]) || ''
                                      }
                                      maxLength={itemAfter.maxLength}
                                      data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                      placeholder={itemAfter.placeholder || ''}
                                      data-removearrows={!!itemAfter.inputNumberRemoveArrows}
                                      disabled={!!itemAfter.disabled}
                                      data-preventautocomplete={itemAfter.preventAutoComplete || false}
                                      readOnly={itemAfter.readOnly || itemAfter.preventAutoComplete || false}
                                    />
                                  )}

                                  {itemAfter.formType === 'select' && (
                                    <Select
                                      id={`item-${itemAfter.name}`}
                                      name={itemAfter.name}
                                      data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                      onChange={itemAfter.selectOnChange || formikHandleChange}
                                      defaultValue={itemAfter.defaultValue || formik.values[itemAfter.name]}
                                      disabled={!!itemAfter.disabled}
                                    >
                                      {!itemAfter.selectRemoveFirstOption && (
                                        <option value={itemAfter.selectFirstValue} hidden>
                                          {itemAfter.selectFirstLabel || ''}
                                        </option>
                                      )}
                                      {itemAfter.options &&
                                        itemAfter.options.map((optionItem, selectIndex) => (
                                          <option
                                            key={`selectOptions-item-${itemAfter.name}-${selectIndex.toString()}`}
                                            // selected={formik.values[itemAfter.name] === optionItem.value}
                                            value={optionItem.value}
                                          >
                                            {optionItem.label}
                                          </option>
                                        ))}
                                    </Select>
                                  )}

                                  {itemAfter.formType === 'checkbox' && (
                                    <Flex flexWrap="nowrap" alignItems="center">
                                      <Checkbox
                                        id={`item-${itemAfter.name}`}
                                        name={itemAfter.name}
                                        type={itemAfter.inputType}
                                        onChange={itemAfter.checkboxOnChange || formikHandleChange}
                                        value="1"
                                        checked={Boolean(formik.values[itemAfter.name])}
                                        disabled={!!itemAfter.disabled}
                                      />

                                      {Boolean(itemAfter.checkboxLabel) && (
                                        <label htmlFor={`item-${itemAfter.name}`}>
                                          <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                            {itemAfter.checkboxLabel}
                                          </Typography>
                                        </label>
                                      )}
                                    </Flex>
                                  )}
                                </Box>
                              );
                            })
                          ) : (
                            <></>
                          )}
                        </Flex>
                      </Flex>

                      {formik.touched[item.name] && formik.errors[item.name] ? (
                        <HelperText type="error">{formik.errors[item.name]}</HelperText>
                      ) : null}
                    </Flex>

                    {!!item.rightLabel && (
                      <Flex display={{ m: 'flex', d: 'none' }} flexGrow={0} mt={2}>
                        <Typography type="type0" color="gray_500">
                          {item.rightLabel}
                        </Typography>
                      </Flex>
                    )}
                  </Flex>
                  {Boolean(item.rightText) && (
                    <Flex flexGrow={0} alignItems="center" height={38} mt={{ m: 1, d: '43px' }}>
                      <Typography type="type0" ml={{ d: 3 }}>
                        <div dangerouslySetInnerHTML={createMarkup(item.rightText)} />
                      </Typography>
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>
        );
      })}
    </>
  );
};

export default FormGenerator;
